import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import { Toaster } from './components/ui/sonner';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import CardsPage from './pages/CardsPage';
import CardEditorPage from './pages/CardEditorPage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import AnalyticsPage from './pages/AnalyticsPage';
import NotificationsPage from './pages/NotificationsPage';
import GeofencesPage from './pages/GeofencesPage';
import BillingPage from './pages/BillingPage';
import SettingsPage from './pages/SettingsPage';
import ScannerPage from './pages/ScannerPage';
import StoreQRPage from './pages/StoreQRPage';
import JoinPage from './pages/JoinPage';
import InstantScanPage from './pages/InstantScanPage';
import WalletPassPage from './pages/WalletPassPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalPage from './pages/LegalPage';
import TermsPage from './pages/TermsPage';

// Admin Pages
import AdminLoginPage from './pages/AdminLoginPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminMerchantsPage from './pages/AdminMerchantsPage';
import AdminMerchantDetailPage from './pages/AdminMerchantDetailPage';
import AdminStatsPage from './pages/AdminStatsPage';

// Layout
import DashboardLayout from './components/DashboardLayout';
import AdminLayout from './components/AdminLayout';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public Route (redirect to dashboard if logged in, unless there's a redirect param)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (user) {
    const redirect = searchParams.get('redirect') || '';
    // Only same-site paths ("/s/LP-…"), never another website.
    const safe = redirect.startsWith('/') && !redirect.startsWith('//') && !redirect.startsWith('/\\');
    return <Navigate to={safe ? redirect : "/dashboard"} replace />;
  }

  return children;
};

// Admin Protected Route
const AdminProtectedRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-900"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div></div>;
  }
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/join/:cardId" element={<JoinPage />} />
      <Route path="/s/:memberCode" element={<InstantScanPage />} />
      <Route path="/wallet/:memberCode" element={<WalletPassPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/confidentialite" element={<PrivacyPage />} />
      <Route path="/mentions-legales" element={<LegalPage />} />
      <Route path="/cgv" element={<TermsPage />} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin" element={<AdminProtectedRoute><AdminLayout /></AdminProtectedRoute>}>
        <Route index element={<AdminDashboardPage />} />
        <Route path="merchants" element={<AdminMerchantsPage />} />
        <Route path="merchants/:id" element={<AdminMerchantDetailPage />} />
        <Route path="stats" element={<AdminStatsPage />} />
      </Route>

      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<DashboardPage />} />
        <Route path="cards" element={<CardsPage />} />
        <Route path="cards/:id" element={<CardEditorPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="customers/:id" element={<CustomerDetailPage />} />
        <Route path="scanner" element={<ScannerPage />} />
        <Route path="store-qr" element={<StoreQRPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="geofences" element={<GeofencesPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="billing/success" element={<BillingPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AdminAuthProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </AdminAuthProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
